import dynamic from 'next/dynamic';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useAuth } from '../../AuthProvider';
import {
  AddAttachments,
  AddMessageLayout,
  CompactAddMessageLayout,
  hasPersonalContactDetails,
  MESSAGE_STATUS,
  PersonalContactDetailsWarningModal,
  TextareaAutoSize,
  TooltipWrapper,
  useMessageDrafts,
  useSendOnEnter,
} from '@zg-rentals/satellite-components';
import { useUserWarnings } from '../../hooks/useUserWarnings';
import type React from 'react';
import { forwardRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  SatelliteConversationMessageType,
  type SatelliteWebLandlordConversationAddMessageRequestDto,
  type SatelliteWebLandlordConversationAddMessageResponseDto,
} from '@zg-rentals/ts-satellite-client';
import { useSatelliteWebLandlordConversationHandler_addMessage } from '@zg-rentals/ts-satellite-client-react-query';
import { getConversationMessagesQueryKey } from '../../hooks/useConversationMessagesQuery';
import {
  type ConversationMessage,
  type ConversationMessagesData,
  FAILED_UI_TREATMENT,
  mapAddMessageResponseToConversationMessage,
  MessageSendFailureReason,
  SENDING_UI_TREATMENT,
  toConversationMessageAttachments,
} from '../../utils/conversationMessageUtils';
import { isMessageTooLongError } from '../../utils/errorUtils';
import {
  emitSendDraftMessageEvent,
  emitSendMessageEvent,
  SEND_MESSAGE_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/sendMessage';
import StringConstants from '../../constants/StringConstants';
import Urls from '../../constants/UrlConstants';
import {
  addMessageInputTextToAppendStore,
  clearAndAppendMessageInputTextStore,
  isAddMessageInputFocusedStore,
  isConversationSearchBarFocusedStore,
} from '../../lib/inboxAddMessageStores';
import { AppContext } from '../../contexts/AppContextProvider';
import formValidationUtils from '../../utils/formValidationUtils';
import stringUtils from '../../utils/stringUtils';
import type { AttachmentsForMessage } from '../MessageAttachments';
import type { AttachmentsUploadedForAddMessage } from '@zg-rentals/satellite-components';
import UploadedAttachmentList from './components/UploadedAttachmentList';
import QuickResponse from './components/QuickResponse';
import MessageListActions from '../MessageListActions';
import useModal from '../../hooks/useModal';
import { ModalType } from '../../types/ModalType';
import { appMarkerLogger } from '../../bootstrap';
import { Box } from '@zillow/constellation-10';
import { emitStoreMessageToLocalStorageEvent } from '../../constants/clickstreamEvents/conversationItem';
import { TRIGGER_LOCATION } from '../../constants/clickstreamEvents/globals';
import type { TriggerLocation } from '../../constants/clickstreamEvents/globals';
import {
  emitOpenPersonalInfoWarningModalEvent,
  emitSendAnywayInPersonalInfoWarningModalEvent,
} from '../../constants/clickstreamEvents/personalInfoWarningModal';

const AttachmentsUploader = dynamic(() => import('../AttachmentsUploader'), {
  loading: () => null,
  ssr: false,
});

const logger = appMarkerLogger.child({ name: 'AddMessage' });

export type SetAttachmentsUploadedForAddMessage = (uploadedAttachments: AttachmentsUploadedForAddMessage) => void;

export type AddMessageQuickResponses = ConversationMessagesData['quickResponses'];

export type AddMessageCompletedCacheUpdate = {
  /** Raw Proxit `addMessage` response plus the client-computed preview (the endpoint doesn't
   *  return `messagePreview`). Enum fields keep their raw UPPER_SNAKE_CASE casing here so the
   *  parent's conversation-list update can coerce `uiTreatmentType` via `toKnownUiTreatmentType`. */
  newMessage: SatelliteWebLandlordConversationAddMessageResponseDto & { messagePreview?: string };
  attachmentsForMessage: AttachmentsForMessage;
};

type Props = {
  conversationId: string;
  listingAlias: string;
  leadId?: string | null;
  clientTimeZone: string;
  fullMessageCountNumber: number;
  hasQuickResponses: boolean;
  quickResponses: AddMessageQuickResponses | null;
  isConversationBlockedByZillow: boolean;
  isPhoneLead: boolean;
  isSpam: boolean;
  /** When 'compact', renders CompactAddMessageLayout with always-visible borders. */
  variant?: 'default' | 'compact';
  /** Trigger location for clickstream (inbox vs lead management). Defaults to inbox. */
  triggerLocation?: TriggerLocation;
  /** Optional callback invoked after writing the new message to the message-list cache.
   *  Allows the parent to perform its own cache updates (e.g. conversation list reorder, renter profile attachments). */
  onAddMessageCompletedCacheUpdate?: (params: AddMessageCompletedCacheUpdate) => void;
};

/**
 * Message composer input for adding messages to a conversation.
 * Supports two variants:
 * - 'default': Full inbox layout with desktop/mobile views, tooltips, quick responses
 * - 'compact': Single-row compact layout with always-visible borders for modals
 */
const AddMessage = forwardRef<HTMLDivElement, Props>(
  (
    {
      conversationId,
      listingAlias,
      leadId,
      clientTimeZone,
      fullMessageCountNumber = 0,
      hasQuickResponses,
      quickResponses,
      isConversationBlockedByZillow,
      isPhoneLead,
      isSpam,
      variant = 'default',
      triggerLocation = TRIGGER_LOCATION.IreneInbox,
      onAddMessageCompletedCacheUpdate,
    }: Props,
    ref,
  ) => {
    const isCompact = variant === 'compact';
    const queryClient = useQueryClient();
    const { isImpersonator } = useContext(AppContext);
    const [inputText, setInputText] = useState('');
    const { user } = useAuth();

    const inboxPageAddMessageInputTextToAppend = addMessageInputTextToAppendStore.useValue();
    const inboxPageIsConversationSearchBarFocused = isConversationSearchBarFocusedStore.useValue();
    const inboxPageIsAddMessageInputFocused = isAddMessageInputFocusedStore.useValue();
    const inboxPageClearAndAppendMessageInputText = clearAndAppendMessageInputTextStore.useValue();
    const { personalContactWarningData } = useUserWarnings();

    const [currentTempMessageId, setCurrentTempMessageId] = useState(String(Date.now()));
    const [uploadedAttachments, setUploadedAttachments] = useState<AttachmentsUploadedForAddMessage>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [isMessageSent, setIsMessageSent] = useState(false);
    const [isQuickResponseLoadedFirstTime, setQuickResponseIsloadedFirstTime] = useState(true);
    const [isMessageInputEmpty, setIsMessageInputEmpty] = useState(true);
    const [selectedQuickResponseID, setSelectedQuickResponseID] = useState('');

    // Callback to track when drafts are saved to localStorage
    const handleDraftSaved = useCallback(() => {
      emitStoreMessageToLocalStorageEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
    }, [user, listingAlias, leadId, triggerLocation]);

    const { clearMessageDrafts, getHasLocalStorageMessageDraft } = useMessageDrafts<
      AttachmentsUploadedForAddMessage[0]
    >({
      conversationId: conversationId || '',
      logger,
      messageInputText: inputText,
      setMessageInputText: setInputText,
      uploadedAttachments,
      setUploadedAttachments,
      onDraftSaved: handleDraftSaved,
    });

    useEffect(() => {
      /**
       * Revoke generated preview urls from AttachmentsUploader to
       * prevent memory leaks, will run on unmount
       * https://developer.mozilla.org/en-US/docs/Web/API/URL/revokeObjectURL
       */
      return () =>
        uploadedAttachments.forEach((uploadedAttachment) => {
          if (uploadedAttachment.generatedPreviewUrl) {
            URL.revokeObjectURL(uploadedAttachment.generatedPreviewUrl);
          }
        });
    }, []);

    const disableComponent = Boolean(isPhoneLead || isImpersonator || isSpam || isConversationBlockedByZillow);
    const hasNoMessageText = formValidationUtils.isInputEmpty(inputText);
    const isAddMessageInputEmpty = hasNoMessageText && uploadedAttachments.length === 0;
    const isSendButtonDisabled = disableComponent || isAddMessageInputEmpty;
    const [shouldFocusAddMessage, setShouldFocusAddMessage] = useState(
      inboxPageIsAddMessageInputFocused && !inboxPageIsConversationSearchBarFocused && !disableComponent,
    );

    useEffect(() => {
      setIsMessageSent(false);
    }, [hasQuickResponses, fullMessageCountNumber]);

    useEffect(() => {
      isAddMessageInputFocusedStore.set(true); // Focus input when attachments are added
    }, [uploadedAttachments]);

    /**
     * If inboxPageAddMessageInputTextToAppend has content, append it to current text
     * then reset inboxPageAddMessageInputTextToAppend to ''.
     *
     * Use a functional update and keep inputText out of the deps: depending on inputText
     * would re-fire this effect (when setInputText lands) while the stale append value is
     * still set, appending the reply multiple times.
     */
    useEffect(() => {
      if (inboxPageAddMessageInputTextToAppend) {
        setInputText((currentInputText) =>
          currentInputText
            ? `${currentInputText} ${inboxPageAddMessageInputTextToAppend}`
            : inboxPageAddMessageInputTextToAppend,
        );
        if (hasQuickResponses) {
          setSelectedQuickResponseID('');
          setIsTyping(true);
        }
        addMessageInputTextToAppendStore.set('');
      }
    }, [inboxPageAddMessageInputTextToAppend, hasQuickResponses]);

    /**
     * If inboxPageClearAndAppendMessageInputText has content, clear the current text in the message input box
     * and append the content to current text. Then reset inboxPageClearAndAppendMessageInputText to ''
     */
    useEffect(() => {
      if (inboxPageClearAndAppendMessageInputText) {
        setInputText(inboxPageClearAndAppendMessageInputText);
        setIsMessageInputEmpty(false);
        clearAndAppendMessageInputTextStore.set('');
      }
    }, [inboxPageClearAndAppendMessageInputText]);

    const { setActiveModal, activeModal, clearModal } = useModal();
    const attachmentsUploaderIsOpened = ModalType.AttachmentsUploader === activeModal;
    const personalContactDetailsWarningModalIsOpened = ModalType.PersonalContactDetailsWarningModal === activeModal;

    /**
     * Allows us to persist attachment data until it is returned from the
     * next getMessages poll in MessageList
     */
    let attachmentsForMessage: AttachmentsForMessage = [];
    // Cache writes + optimistic rollback live in the per-call `onSuccess`/`onError` (in
    // `sendMessage`) so they can close over this send's `optimisticMessageId` and attachments.
    // The hook-level `meta.context` drives automatic error logging.
    const { mutate: addMessageMutation } = useSatelliteWebLandlordConversationHandler_addMessage({
      meta: {
        context: {
          api: 'addMessage',
          details: 'AddMessage - Add message failed. User seeing failed message treatment',
          conversationId,
          listingAlias,
        },
      },
    });

    const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputText(e.target.value);
      if (hasQuickResponses) {
        setQuickResponseIsloadedFirstTime(false);
        setSelectedQuickResponseID('');
      }
      if (e.target.value === '') {
        if (hasQuickResponses) {
          setIsTyping(false);
        }
        setIsMessageInputEmpty(true);
      } else {
        if (hasQuickResponses) {
          setIsTyping(true);
        }
        setIsMessageInputEmpty(false);
      }
    };

    const { onKeyDown: handleKeyDown, placeholder: placeholderText } = useSendOnEnter({
      onSend: () => sendMessage(),
      canSend: !isSendButtonDisabled,
      isCompact,
    });

    /**
     * Add Message to conversation flow
     * 1) Check for personal contact details and show warning modal if detected
     * 2) Add optimistic message to the conversation in the cache
     * 3) Kick off the Proxit addMessage mutation (React Query)
     * 4a) If success, remove optimistic message from cache
     * 4b) If failure, change optimistic message type from 'sending' to 'failed' to change UI
     */
    const sendMessage = (bypassWarning = false) => {
      if (isAddMessageInputEmpty) {
        return;
      }

      // Check for personal contact details unless bypassing the warning
      if (!bypassWarning && hasPersonalContactDetails(inputText)) {
        setActiveModal(ModalType.PersonalContactDetailsWarningModal);
        emitOpenPersonalInfoWarningModalEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
        return;
      }

      if (hasQuickResponses) {
        setIsMessageSent(true);
      }

      const messageText = inputText;
      const { attachmentsForOptimisticMessage = [], zDocIds = [] } = uploadedAttachments.length
        ? getAttachmentDataForAddMessageMutation(uploadedAttachments)
        : {};
      attachmentsForMessage = attachmentsForOptimisticMessage;

      const hasMessageDraft = getHasLocalStorageMessageDraft();
      if (hasMessageDraft) {
        emitSendDraftMessageEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
      }

      clearMessageDrafts();

      // Optimistic UI
      const messagePreview = getOptimisticMessagePreview({
        attachments: attachmentsForMessage,
        message: messageText,
      });
      setCurrentTempMessageId(String(Date.now()));
      const optimisticMessageId = `temp-${currentTempMessageId}`;
      const optimisticMessage = getOptimisticMessage({
        conversationId: conversationId || '',
        message: messageText,
        messagePreview,
        messageDateMs: currentTempMessageId,
        messageId: optimisticMessageId,
        attachments: attachmentsForOptimisticMessage,
      });

      // Optimistically append the new message to the React Query messages cache (replaces
      // older "in-flight" temp messages so we don't accumulate them).
      const messagesQueryKey = getConversationMessagesQueryKey({
        conversationId: conversationId || '',
        clientTimeZone,
      });
      queryClient.setQueryData<ConversationMessagesData>(messagesQueryKey, (existing) => {
        if (!existing) return existing;
        const tempMessages: Array<ConversationMessage> = [];
        const sentMessages = existing.conversation.filter((message) => {
          const isTemp = message.uiTreatmentType?.includes('-');
          if (isTemp) tempMessages.push(message);
          return !isTemp;
        });
        return {
          ...existing,
          conversation: [...sentMessages, ...tempMessages, optimisticMessage],
        };
      });

      const postData: SatelliteWebLandlordConversationAddMessageRequestDto = {
        conversationId: conversationId!,
        message: messageText,
        uploadedZdocIds: zDocIds,
      };

      if (hasQuickResponses && selectedQuickResponseID) {
        postData.quickResponseSuggestionId = selectedQuickResponseID;
      }

      addMessageMutation(
        { postData },
        {
          onSuccess(addMessageResponse) {
            // All post-success work lives in this per-call `onSuccess` so it can close over this
            // send's optimistic id + attachments: cache insert, optimistic removal, parent
            // callback, clickstream, and state reset.
            setCurrentTempMessageId(String(Date.now()));

            const messagePreview = getOptimisticMessagePreview({
              attachments: attachmentsForMessage,
              message: addMessageResponse.message || '',
            });

            // Replace the optimistic temp message with the real server-returned message in the
            // React Query messages cache so the new message appears immediately, before the
            // next `getMessages` poll runs. Preserve any FAILED items at the tail.
            //
            // Also drop any cache entry already carrying the real `messageId`: a poll or
            // websocket-driven refetch can race ahead of this callback and pre-populate the
            // cache with the just-sent message. Apollo's normalized cache used to dedupe by
            // `__typename + messageId` for free; React Query has no normalization so we dedupe
            // explicitly here, otherwise the message appears twice in the list.
            const realMessageId = addMessageResponse.messageId;
            const insertedMessage = mapAddMessageResponseToConversationMessage(addMessageResponse, {
              attachments: toConversationMessageAttachments(attachmentsForMessage),
              messagePreview,
            });
            queryClient.setQueryData<ConversationMessagesData>(messagesQueryKey, (existing) => {
              if (!existing) return existing;
              const failedMessages: Array<ConversationMessage> = [];
              const keptMessages = existing.conversation.filter((message) => {
                // Drop the optimistic message we just sent (it's getting replaced).
                if (message.messageId === optimisticMessageId) return false;
                // Drop a duplicate of the real message if it slipped in via a concurrent poll.
                if (realMessageId && message.messageId === realMessageId) return false;
                // Pull FAILED messages aside so they stay at the tail.
                if (message.messageId?.includes(MESSAGE_STATUS.FAILED)) {
                  failedMessages.push(message);
                  return false;
                }
                return true;
              });
              return {
                ...existing,
                conversation: [...keptMessages, insertedMessage, ...failedMessages],
              };
            });

            // Parent (MessageList / RenterMessagingModal) handles conversation-list reorder and
            // attachment metadata cache update. Pass the raw response plus the locally-computed
            // preview so the parent's `toKnownUiTreatmentType` still sees the raw Proxit enum.
            onAddMessageCompletedCacheUpdate?.({
              newMessage: { ...addMessageResponse, messagePreview },
              attachmentsForMessage,
            });

            emitSendMessageEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });

            if (hasQuickResponses) {
              setSelectedQuickResponseID('');
              setIsTyping(false);
            }
          },
          onError(error) {
            // The error itself is logged automatically via the mutation's `meta.context`. A too-long
            // rejection is tagged so the failed bubble suppresses the pointless retry.
            const sendFailureReason = isMessageTooLongError(error, messageText)
              ? MessageSendFailureReason.MessageTooLong
              : undefined;
            queryClient.setQueryData<ConversationMessagesData>(messagesQueryKey, (existing) => {
              if (!existing) return existing;
              return {
                ...existing,
                conversation: existing.conversation.map((m) =>
                  m.messageId === optimisticMessageId
                    ? { ...m, uiTreatmentType: FAILED_UI_TREATMENT, sendFailureReason }
                    : m,
                ),
              };
            });
          },
        },
      );
    };

    const handleQuickResponeSelect = (selectedQuickResponseValue: string | null | undefined) => {
      setSelectedQuickResponseID(selectedQuickResponseValue || '');
    };

    // Keep the draft preserved by not clearing the input
    const handlePersonalContactWarningGoBack = () => {
      clearModal();
    };

    const handlePersonalContactWarningSendAnyway = () => {
      clearModal();
      emitSendAnywayInPersonalInfoWarningModalEvent({
        user,
        listing: { listingAlias },
        triggerLocation,
        lead: { leadId },
      });
      sendMessage(true);
    };

    const handleFocus = () => {
      isAddMessageInputFocusedStore.set(true);
      setShouldFocusAddMessage(true);
    };

    const handleBlur = () => {
      isAddMessageInputFocusedStore.set(false);
      setShouldFocusAddMessage(false);
    };

    const handleRemoveUploadedAttachment = useCallback(
      (zDocId: string) => {
        const filteredUploads = uploadedAttachments.filter((attachment) => attachment.zDocId !== zDocId);
        setUploadedAttachments(filteredUploads);
      },
      [uploadedAttachments],
    );

    const isAddAttachmentsDisabled = Boolean(isPhoneLead || isSpam || isConversationBlockedByZillow);
    const tooltipText = useMemo(() => {
      if (isPhoneLead) {
        return StringConstants.TOOLTIP.PHONE_LEAD;
      }
      if (isImpersonator) {
        return StringConstants.TOOLTIP.IMPERSONATION;
      }
      if (isConversationBlockedByZillow) {
        return StringConstants.TOOLTIP.BLOCKED_BY_ZILLOW;
      }
      if (isSpam) {
        return StringConstants.TOOLTIP.REPORTED;
      }
      return '';
    }, [isSpam, isPhoneLead, isImpersonator, isConversationBlockedByZillow]);

    const containsUploadedAttachments = !!uploadedAttachments.length;
    const displayQuickResponseCTAs =
      hasQuickResponses && !isTyping && !attachmentsUploaderIsOpened && !containsUploadedAttachments && !isMessageSent;

    // ── Shared sub-elements used by both variants ─────────────────────────

    const textareaElement = (
      <TextareaAutoSize
        ariaLabel="Message input"
        isDisabled={disableComponent}
        onBlur={handleBlur}
        onChange={handleTextAreaChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholderText}
        rows={1}
        shouldFocus={shouldFocusAddMessage}
        value={inputText}
      />
    );

    const attachmentsButton = (
      <AddAttachments
        bordered={isCompact}
        guidelinesVariant={isCompact ? 'compact' : 'default'}
        isDisabled={isAddAttachmentsDisabled}
        setIsAttachmentsUploaderOpen={() => setActiveModal(ModalType.AttachmentsUploader)}
      />
    );

    const attachmentsList = !!uploadedAttachments.length && (
      <UploadedAttachmentList
        uploadedAttachments={uploadedAttachments}
        handleRemoveUploadedAttachment={handleRemoveUploadedAttachment}
        listingAlias={listingAlias}
        leadId={leadId}
        triggerLocation={triggerLocation}
      />
    );

    const quickResponseElement = (
      <QuickResponse
        quickResponses={quickResponses}
        isQuickResponseLoadedFirstTime={isQuickResponseLoadedFirstTime}
        isMessageInputEmpty={isMessageInputEmpty}
        onQuickResponseSelect={handleQuickResponeSelect}
        listingAlias={listingAlias}
        leadId={leadId}
        triggerLocation={triggerLocation}
      />
    );

    const compactAddMessageContent = (
      <Box
        css={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TooltipWrapper wrapInTooltip={disableComponent} tooltipText={tooltipText}>
          <Box css={{ width: '100%', '& textarea': { lg: { boxShadowColor: 'border.neutral' } } }}>
            <CompactAddMessageLayout
              ref={ref}
              isSendDisabled={isSendButtonDisabled}
              onSend={sendMessage}
              leftButtons={attachmentsButton}
              sendMessageDataEventId={SEND_MESSAGE_EVENT_TYPE_ID}
              bottomContent={
                attachmentsList ? (
                  <Box css={{ '& > div': { marginTop: 'tighter', paddingBottom: 0 } }}>{attachmentsList}</Box>
                ) : undefined
              }
            >
              {textareaElement}
            </CompactAddMessageLayout>
          </Box>
        </TooltipWrapper>
        {displayQuickResponseCTAs && (
          <Box css={{ flexShrink: 0, marginTop: 'default', marginInline: '-default', marginBottom: '-default' }}>
            {quickResponseElement}
          </Box>
        )}
      </Box>
    );

    const defaultAddMessageContent = (
      <>
        <MessageListActions
          conversationId={conversationId}
          listingAlias={listingAlias}
          leadId={leadId}
          clientTimeZone={clientTimeZone}
          variant={variant}
          triggerLocation={triggerLocation}
        />
        <TooltipWrapper wrapInTooltip={disableComponent} tooltipText={tooltipText}>
          <AddMessageLayout
            ref={ref}
            isSendDisabled={isSendButtonDisabled}
            onSend={sendMessage}
            leftButtons={attachmentsButton}
            sendMessageDataEventId={SEND_MESSAGE_EVENT_TYPE_ID}
          >
            {textareaElement}
            {attachmentsList}
          </AddMessageLayout>
        </TooltipWrapper>
        {displayQuickResponseCTAs && quickResponseElement}
      </>
    );

    return (
      <>
        {attachmentsUploaderIsOpened && (
          <AttachmentsUploader
            variant={isCompact ? 'compact' : 'default'}
            existingUploadedAttachments={uploadedAttachments}
            setAttachmentsUploadedForAddMessage={setUploadedAttachments}
          />
        )}

        {personalContactDetailsWarningModalIsOpened && (
          <PersonalContactDetailsWarningModal
            warningData={personalContactWarningData}
            onGoBackClick={handlePersonalContactWarningGoBack}
            onSendAnywayClick={handlePersonalContactWarningSendAnyway}
            fallbackLinkUrl={Urls.PERSONAL_CONTACT_WARNING_SCAM_PREVENTION}
          />
        )}

        {isCompact ? compactAddMessageContent : defaultAddMessageContent}
      </>
    );
  },
);

AddMessage.displayName = 'AddMessage';
export default AddMessage;

const getOptimisticMessage = ({
  conversationId,
  messageDateMs,
  message,
  messagePreview,
  messageId,
  attachments,
}: {
  conversationId: string;
  messageDateMs: string;
  message: string;
  messagePreview: string;
  messageId: string;
  attachments: AttachmentsForMessage;
}): ConversationMessage => ({
  aiAutoResponseType: null,
  attachments: toConversationMessageAttachments(attachments),
  conversationId,
  isMessageOwner: true,
  message,
  messageDateMs,
  messageId,
  messageLink: { url: '', text: '' },
  messagePreview,
  messageType: SatelliteConversationMessageType.LISTING_MESSAGE,
  senderName: '',
  title: '',
  uiTreatmentType: SENDING_UI_TREATMENT,
});

const getOptimisticMessagePreview = ({
  attachments,
  message,
}: {
  attachments: AttachmentsForMessage;
  message: string;
}) => {
  const shouldShowAttachmentTextPreview = attachments.length && !message;
  const attachmentTextPreview = `${attachments.length} ${stringUtils.pluralize('attachment', attachments.length)}`;
  return shouldShowAttachmentTextPreview ? attachmentTextPreview : message;
};

const getAttachmentDataForAddMessageMutation = (uploadedAttachments: AttachmentsUploadedForAddMessage) => {
  const zDocIds: Array<string> = [];
  const attachmentsForOptimisticMessage: AttachmentsForMessage = uploadedAttachments.map((uploadedAttachment) => {
    const { file, generatedPreviewUrl, zDocId } = uploadedAttachment;
    const attachmentAdded = new Date().getTime();

    if (zDocId) {
      zDocIds.push(zDocId);
    }

    return {
      attachmentAddedMs: attachmentAdded.toString(),
      attachmentMetadataStatus: 'complete',
      downloadUrl: '',
      fileName: file.name,
      fileSizeBytes: file.size,
      fileType: file.type,
      isAttachmentDeleted: false,
      isAttachmentExpired: false,
      previewUrl: generatedPreviewUrl,
      zDocId,
    };
  });

  return {
    attachmentsForOptimisticMessage,
    zDocIds,
  };
};
